import { backupDataSchema, validateBackupRelations } from '../domain/schemas';
import { canonicalizeCloudSchoolData } from '../domain/cloud';
import type {
  AppSettings,
  BackupData,
  CloudSyncMetadata,
  Locale,
  ThemeMode,
} from '../domain/types';
import { allSchoolTables, DataValidationError, loadAllSchoolData } from '../data/repositories';
import { db, initializeDatabase, type WaschPilotDatabase } from '../data/database';

export interface DevicePreferences {
  language: Locale;
  theme: ThemeMode;
  welcomeDismissed: boolean;
}

export interface CloudConfigurationSnapshot {
  data: BackupData;
  revision: number;
  updatedAt: string;
}

function storedLanguage(): Locale | null {
  if (typeof localStorage === 'undefined') return null;
  const value = localStorage.getItem('waschpilot-language');
  return value === 'de' || value === 'ar' ? value : null;
}

function storedTheme(): ThemeMode | null {
  if (typeof localStorage === 'undefined') return null;
  const value = localStorage.getItem('waschpilot-theme');
  return value === 'system' || value === 'light' || value === 'dark' ? value : null;
}

export function readDevicePreferences(settings: AppSettings | undefined): DevicePreferences {
  return {
    language: storedLanguage() ?? settings?.language ?? 'de',
    theme: storedTheme() ?? settings?.theme ?? 'system',
    welcomeDismissed: settings?.welcomeDismissed ?? false,
  };
}

export function devicePreferencesFromData(data: BackupData): DevicePreferences {
  const settings = data.settings[0];
  if (!settings) throw new DataValidationError('Application settings are missing.');
  return {
    language: settings.language,
    theme: settings.theme,
    welcomeDismissed: settings.welcomeDismissed,
  };
}

export function mergeDevicePreferences(
  input: BackupData,
  preferences: DevicePreferences,
): BackupData {
  const data = structuredClone(input);
  const settings = data.settings[0];
  if (!settings) throw new DataValidationError('Application settings are missing.');
  data.settings[0] = { ...settings, ...preferences };
  return data;
}

/** Removes per-device choices before a school configuration is sent to D1. */
export function canonicalizeCloudData(input: BackupData): BackupData {
  return canonicalizeCloudSchoolData(input);
}

/**
 * Validates and replaces the complete cached school snapshot and its revision in one transaction.
 * The device language, theme and welcome state are deliberately never copied from the cloud.
 */
export async function cacheCloudConfiguration(
  snapshot: CloudConfigurationSnapshot,
  database: WaschPilotDatabase = db,
  preferencesOverride?: DevicePreferences,
): Promise<BackupData> {
  if (!Number.isSafeInteger(snapshot.revision) || snapshot.revision < 1) {
    throw new DataValidationError('The cloud revision is invalid.');
  }
  const parsed = backupDataSchema.parse(snapshot.data);
  const relationIssues = validateBackupRelations(parsed);
  if (relationIssues.length > 0) {
    throw new DataValidationError('The cloud data contains invalid relationships.', relationIssues);
  }

  await initializeDatabase(database);
  const tables = allSchoolTables(database);
  let appliedData: BackupData | null = null;

  await database.transaction('rw', [...tables, database.cloudSyncMetadata], async () => {
    const currentMetadata = await database.cloudSyncMetadata.get('school-config');
    // A slower response from an older request must never roll the shared cache backwards.
    if (currentMetadata && currentMetadata.revision >= snapshot.revision) return;

    // Read the device settings while holding the same transaction that replaces the school data.
    // This prevents a concurrent local preference update from being lost between read and write.
    const currentSettings = await database.settings.get('app');
    const preferences = preferencesOverride ?? readDevicePreferences(currentSettings);
    const data = mergeDevicePreferences(parsed, preferences);
    const metadata: CloudSyncMetadata = {
      id: 'school-config',
      revision: snapshot.revision,
      lastSyncedAt: new Date().toISOString(),
      source: 'cloud',
    };

    await Promise.all(tables.map((table) => table.clear()));
    await database.settings.bulkPut(data.settings);
    await database.products.bulkPut(data.products);
    await database.dosageTables.bulkPut(data.dosageTables);
    await database.waterHardness.bulkPut(data.waterHardness);
    await database.containers.bulkPut(data.containers);
    await database.measuringCups.bulkPut(data.measuringCups);
    await database.conversionConfigurations.bulkPut(data.conversionConfigurations);
    await database.calibrationMeasurements.bulkPut(data.calibrationMeasurements);
    await database.temperatureGuidance.bulkPut(data.temperatureGuidance);
    await database.recommendationRules.bulkPut(data.recommendationRules);
    await database.stainGuides.bulkPut(data.stainGuides);
    await database.cloudSyncMetadata.put(metadata);
    appliedData = data;
  });
  return appliedData ?? loadAllSchoolData(database);
}

export async function readCloudSyncMetadata(
  database: WaschPilotDatabase = db,
): Promise<CloudSyncMetadata | undefined> {
  await initializeDatabase(database);
  return database.cloudSyncMetadata.get('school-config');
}
